import { twMerge } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import {
  useAssessedCountData,
  useAssessmentMode,
  useAssessmentNavData,
  useAssessmentPublishActions,
} from "../../utils/assessment";
import { useAv } from "../../utils/av";
import { useQueryParam } from "../../utils/routing/hooks/use-query-params";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import {
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlay,
} from "components/constants";
import { BaseFooter } from "components/footer/BaseFooter";
import EndClassMenu from "components/footer/EndClassMenu";
import { PublishActionButton } from "components/footer/assessment/PublishActionButton";
import { AssessmentExitDialog } from "components/materials/page/assessment/AssessmentExitDialog";
import { showUnpublishAssessmentModal } from "components/materials/page/assessment/helpers";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { ProgressBar } from "mds/components/ProgressBar";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { RoundedButton, RoundedButtonProps } from "mds/components/buttons/RoundedButton";
import { useIsSmOrLarger } from "mds/hooks/use-responsive";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CopySendArrowIcon,
  ReactionTransparentVoteYesIcon,
} from "mds/icons";
import { useAssessmentGradingFormContext } from "providers/AssessmentGradingFormProvider";
import { useAppSelector } from "store/index";
import {
  pageSelectors,
  selectCurrentClassUserIsInPersonClass,
  selectCurrentPageId,
  selectCurrentSectionUserId,
  selectShouldShowModalOnExitAssessmentMode,
  submissionSelectors,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";

const LazyVideoMicControls = withActiveAv(() => import("components/av/VideoMicControls"));

const SUBMIT_BTN_ATTRS: Omit<RoundedButtonProps, "kind"> = {
  form: "assessment-grading-form",
  type: "submit",
  name: "submitSource",
};

export const AssessmentProgressBar = () => {
  const { isAiAssessmentLoading: isLoading, saveConfirmation } = useAssessmentGradingFormContext();
  const selectedPageId = useAppSelector(selectCurrentPageId);
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, selectedPageId),
    ["title", "assessments_published_at"],
  );
  const { onBack, onNext, prevId, isLastPanel } = useAssessmentNavData(selectedPageId);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const { isInVirtualClass } = useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;

  const { stopAssessing, stopAssessingAndMaybeShowAssessmentSummary } =
    useAssessmentMode(selectedPageId);
  const { publishGrades, unpublishGrades } = useAssessmentPublishActions(selectedPageId);

  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useObjectSelector(
    (s) => submissionSelectors.selectById(s, submissionId),
    ["id"],
  );

  const isNextDisabled = !sectionUserId || isLastPanel || isLoading;
  const isPrevDisabled = !sectionUserId || !prevId || isLoading;

  const { count, total } = useAssessedCountData(selectedPageId);

  const isMobile = !useIsSmOrLarger();

  const shouldShowModalOnExitAssessmentMode = useAppSelector(
    selectShouldShowModalOnExitAssessmentMode,
  );

  const {
    value: exitDialogIsOpen,
    setTrue: openExitDialog,
    setFalse: closeExitDialog,
  } = useBoolean(false);

  const openExitDialogOrExitAssessmentMode = () => {
    if (shouldShowModalOnExitAssessmentMode) {
      openExitDialog();
    } else {
      stopAssessingAndMaybeShowAssessmentSummary();
    }
  };

  if (!page || !selectedPageId) return null;

  const prevBtnAttrs: Omit<RoundedButtonProps, "kind"> = submission
    ? { ...SUBMIT_BTN_ATTRS, value: "prev" }
    : { onClick: (e) => onBack(e as React.MouseEvent<HTMLButtonElement>) };
  const nextBtnAttrs: Omit<RoundedButtonProps, "kind"> = submission
    ? { ...SUBMIT_BTN_ATTRS, value: "next" }
    : { onClick: onNext };

  const darkMode = isInClassSession;

  const onPublishGrades = async () => {
    await publishGrades();
    return stopAssessing({ [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.AssessmentSummary });
  };

  const onUnpublishGrades = () => {
    showUnpublishAssessmentModal(count, () => {
      unpublishGrades();
      stopAssessing();
    });
  };

  return (
    <BaseFooter darkMode={darkMode}>
      <LazyVideoMicControls />
      <div
        className={twMerge(
          "flex w-full items-center justify-center gap-3 text-sm font-medium text-black-tint-40",
          darkMode && "text-white",
        )}
      >
        <PublishActionButton
          isMobile={isMobile}
          pageId={selectedPageId}
          sectionUserId={sectionUserId}
          onPublishGrades={onPublishGrades}
          onUnpublishGrades={onUnpublishGrades}
        />

        <RoundedButton
          className={
            saveConfirmation?.source === "prev"
              ? "cursor-default disabled:border-green-shade-20 disabled:bg-green-shade-20 disabled:text-white"
              : undefined
          }
          disabled={isPrevDisabled}
          eventName="Assessment - Previous"
          eventProps={{ pageId: selectedPageId, submissionId: submission?.id }}
          kind={saveConfirmation?.source === "prev" ? "success" : "secondary"}
          {...prevBtnAttrs}
        >
          <span className="relative">
            <span className={saveConfirmation?.source === "prev" ? "invisible" : undefined}>
              <IconText
                iconStart={!isMobile ? <ChevronLeftIcon /> : undefined}
                text={t("common.previous")}
              />
            </span>
            {saveConfirmation?.source === "prev" && (
              <span className="animate-save-fade absolute inset-0 flex items-center justify-center gap-2">
                <ReactionTransparentVoteYesIcon className="text-white" />
                {t("assessment.saved")}
              </span>
            )}
          </span>
        </RoundedButton>

        <TooltipTrigger
          content={t("assessment.assessment_progress_bar_tooltip", {
            count,
            total,
          })}
        >
          <div
            className={twMerge(
              "flex items-center justify-end whitespace-nowrap",
              !isMobile && "w-[60px]",
            )}
          >
            {t("common.index_of_total_counter", { index: count, total })}
          </div>
        </TooltipTrigger>

        {!isMobile && <ProgressBar count={count} total={total} />}

        <Tooltip
          content={
            !sectionUserId ? t("assessment_card.cannot_assess_without_enrollment") : undefined
          }
        >
          <RoundedButton
            className={
              saveConfirmation?.source === "next"
                ? "min-w-28 cursor-default disabled:border-green-shade-20 disabled:bg-green-shade-20 disabled:text-white"
                : "min-w-28"
            }
            disabled={isNextDisabled}
            eventName="Assessment - Next"
            eventProps={{ pageId: selectedPageId, submissionId: submission?.id }}
            kind="success"
            {...nextBtnAttrs}
          >
            <span className="relative">
              <span className={saveConfirmation?.source === "next" ? "invisible" : undefined}>
                <IconText
                  iconEnd={!isMobile ? <ChevronRightIcon /> : undefined}
                  text={t("common.next")}
                />
              </span>
              {saveConfirmation?.source === "next" && (
                <span className="animate-save-fade absolute inset-0 flex items-center justify-center gap-2">
                  <ReactionTransparentVoteYesIcon className="text-white" />
                  {t("assessment.saved")}
                </span>
              )}
            </span>
          </RoundedButton>
        </Tooltip>

        <RoundedButton
          eventName="Assessment - Stop"
          eventProps={{
            pageId: selectedPageId,
            pageTitle: page?.title,
            submissionId: submission?.id,
          }}
          kind="secondary"
          onClick={openExitDialogOrExitAssessmentMode}
        >
          <IconText
            iconStart={<CopySendArrowIcon />}
            text={!isMobile ? t("assessment.finish_later") : undefined}
          />
        </RoundedButton>
        <AssessmentExitDialog
          count={count}
          open={exitDialogIsOpen}
          published={!!page?.assessments_published_at}
          total={total}
          onClose={closeExitDialog}
          onConfirmExit={stopAssessingAndMaybeShowAssessmentSummary}
        />
      </div>
      {isInClassSession && (
        <div className="pl-2">
          <EndClassMenu />
        </div>
      )}
    </BaseFooter>
  );
};
